import { useRef, type ReactNode } from "react";
import { Collapse } from "@mui/material";
import { MoreInformationSection } from "../sections/MoreInformationSection";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";
import type { DrawerSectionMap } from "./types";
import { NEEDS_YOU_LABEL } from "~/common/workflows/statusLabels";

export interface HitlDrawerBodyProps {
  sections: DrawerSectionMap;
  findingsBlock?: ReactNode | null;
  /**
   * The phase / milestone main stream (running-drawer). A ReactNode, not a
   * snapshot: the bodies stay presentation-only and none of them imports
   * `phaseModel.ts`, so the adapter keeps its single-reader rule.
   *
   * Rendered FIRST when present, and it SUPPRESSES the flat steps chip it
   * replaces.
   */
  phaseStream?: ReactNode | null;
  /**
   * The step view (story 26739) — the "what do I do right now" pane for this
   * moment in the run. A ReactNode, not a model: the bodies stay
   * presentation-only, so the host derives the view from `push.status` on
   * every render and hands the finished node down.
   *
   * When present it takes the hero position (ahead of everything else) and,
   * like `phaseStream`, SUPPRESSES the flat steps chip it replaces. It does
   * NOT replace `input_needed`: the shared `DecisionCard` is still the one
   * commit affordance, and it moves BELOW the step view rather than being
   * re-implemented inside it. A step view that owned its own pills would be a
   * second submit path around `useHitlSubmitLatch`, which is the double-submit
   * the shared card exists to prevent.
   * Default `undefined` reproduces today's markup exactly — that is what makes
   * the `emuw-step-views` flag-off path a true rollback rather than a second
   * code path.
   */
  stepView?: ReactNode | null;
  /**
   * The carrier-evidence decision card (running-drawer). Present ONLY when the
   * run carries an `evidence_decision`, never gated on a status literal —
   * gating on the parked status makes the early-override path unreachable
   * (AC-6). Absent prop reproduces today's markup byte for byte.
   */
  decisionCard?: ReactNode | null;
  /** The run record shown behind the more-information entry point. The SAME
   *  record the standalone /workflows/runs/<id> page renders. */
  record: RunRecordBodyProps;
  /** Copy-id / kill-session links — More information pane only. */
  debugActions?: ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, not here. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}

export function HitlDrawerBody({
  sections,
  findingsBlock,
  phaseStream,
  stepView,
  decisionCard,
  record,
  debugActions,
  moreInfoOpen = false,
  onMoreInfoClose,
}: HitlDrawerBodyProps) {
  // Keep the last non-null input_needed rendered during the Collapse close
  // animation. Without this, sections.input_needed -> null swaps the children
  // to <span /> synchronously, so Collapse measures 0 height and animates 0->0
  // (visually: a hard snap with content racing up to fill the space).
  const lastInputNeeded = useRef<ReactNode | null>(null);
  if (sections.input_needed != null) {
    lastInputNeeded.current = sections.input_needed;
  }

  const inputNeededBlock = (
    <>
      {phaseStream && sections.input_needed ? (
        <div
          data-testid="hitl-needs-you-banner"
          role="status"
          className="mb-2 flex items-center gap-2 rounded-md border border-primary-warning-300 bg-primary-warning-50 px-3 py-2"
        >
          <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full bg-primary-warning-600"
          />
          <p className="text-2xs font-semibold uppercase tracking-wide text-primary-warning-800">
            {NEEDS_YOU_LABEL}
          </p>
        </div>
      ) : null}
      <Collapse in={!!sections.input_needed} unmountOnExit timeout={400} appear>
        <div className="mb-[11px]">
          {sections.input_needed ?? lastInputNeeded.current ?? <span />}
        </div>
      </Collapse>
    </>
  );

  return (
    <>
      {/* running-drawer: the prompt awaiting the AM sits ABOVE the phase
          stream so a waiting gate is the first thing they see — not buried
          under the milestone list. Flag-off keeps today's order. */}
      {!stepView && phaseStream ? inputNeededBlock : null}

      {/* The phase stream is the ALWAYS-ON progress display (operator
          directive 2026-09-16): a step view no longer displaces it. Both
          render, stream FIRST, so run progress stays the first thing in the
          body and the step view's reassurance sits under it. */}
      {phaseStream ?? null}
      {stepView ?? null}

      {/* With a step view the pills belong DIRECTLY under it — under the brief
          or the restatement stack, which is where the artboards put them.
          They used to land after the decision card and the request, which put
          the carrier-evidence dropzone ABOVE the instruction that tells the AM
          what to do with it: on `carrier_action_instruction` the pane read
          "You are attaching the evidence" first and "this step is manual —
          choose I've contacted the carrier" two sections below. Instruction
          first, then where the evidence goes. */}
      {stepView ? inputNeededBlock : null}

      {/* running-drawer: the carrier-evidence decision, in the PRIMARY body.
          Never placed behind the more-information entry point (AC-7). Phases
          sit above this dropzone. */}
      {decisionCard ?? null}

      {/* running-drawer: the submitted request, in the PRIMARY body — on every
          family, including HITL and terminal (AC-8 / AC-9). */}
      {sections.request ?? null}

      {/* With no step view or phase stream to sit under, input_needed stays
          here, after the request. */}
      {!stepView && !phaseStream ? inputNeededBlock : null}

      <MoreInformationSection
        record={record}
        open={moreInfoOpen}
        onClose={() => onMoreInfoClose?.()}
        extraBlocks={
          <>
            {debugActions ?? null}
          </>
        }
      />

      <Collapse in={!!findingsBlock} unmountOnExit timeout={600}>
        {findingsBlock ?? <span />}
      </Collapse>
    </>
  );
}

export default HitlDrawerBody;
